import React, { useState } from "react";
import TeamHeader from "../TeamHeader/TeamHeader";
import MainHub from "../MainHub/MainHub";

// Assets
import tempFlow from "./assets/hardware-flow.png";
import aiFlow from "./assets/ai-flow.png";
import serverFlow from "./assets/server-flow.png";
import toaFlow from "./assets/toa-flow.png";
import gisFlow from "./assets/gis-flow.png";
import marketingFlow from "./assets/marketing-flow.png";
import manufactuingFlow from "./assets/manufacturing-flow.png";

// Icons
import { RiArrowDownSFill, RiArrowUpSFill } from "react-icons/ri";

const MainHubPage = () => {
  const [selectedRegions, setSelectedRegions] = useState<string[]>(["All"]);
  const [openSlips, setOpenSlips] = useState<string[]>([]); // ← allow multiple

  const toggleSlip = (key: string) => {
    setOpenSlips((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Placeholder stats (unchanged)
  const placeholderStats = {
    strikes: {
      cloudToGround: 36900,
      detectionEfficiency: 80,
      locationAccuracy: 90,
    },
    fires: {
      lightningIgnited: 364,
      lccPercentage: 80,
      lccRatio: 30.4,
      hrlRatingMedian: 24,
    },
  };

  // Later fetched from Django
  const flowSections: {
    [key: string]: {
      title: string;
      paragraphs: string[];
      bullets?: string[];
      image: string;
    };
  } = {
    hardware: {
      title: "Hardware",
      paragraphs: [
        "The Hardware team is at the core of the FNN, focusing on the detectors. They are responsible for four key areas: developing, improving, maintaining, and monitoring all detectors installed worldwide.",
      ],
      bullets: [
        "The development team seeks innovative solutions to enhance the robustness and stability of the detectors, creating new firmware and hardware for the upcoming season. The team is currently working on the 2026 Firmware to support FFS in the next fire season starting April 1, 2026.",
        "The monitoring system is crucial, providing real-time information on the current health of each detector. It allows for tracking various parameters, such as files transferred per hour, running algorithms, and versions of both hardware and software. At FFS we currently have 55 v.5.2 detectors deployed. To meet the uptime target of 90% at all times, DLink router and power supply upgrades are being implemented in the FFS detectors."
      ],
      image: tempFlow,
    },
    server: {
      title: "Server",
      paragraphs: [
        "The Server Team is responsible for real-time analysis within the Florida pipeline, focusing on processing HF and LF bin files alongside peak results from edge stations. Computational resources used in the FFS pipeline include:",
      ],
      bullets: [
        "FNN Edge [AMD FPGA v.3.1.3]: FPGA Edge processing for peak detection.",
        "Data uplink [Verizon]: 4G connectivity from to edge to the cloud.",
        "Cloud computing: Centralized cloud processing for Cluster[v.5.2] and LS Match [v.3.2] algorithms.",
        "Large Data storage on FNN server [Physical server rsyncs v. 1.7]: Local server at the FNN HQ that is used to store large .bin data files and Process LCC [v.3.0].",
        "PostGreSQL Microsoft Azure Database for load balancing and scallability storing and retrieving a year's data.",
        "AI high performance computing [HiPerGator]: System used for AI training of the IC/CG algorithm and the HRL rating algorithm.",
        "AI HRL[v.2] for real time lightning pairing and ai HRL assignment for lightning data.",
        "HRL Historical: to update the historical Esri Feature Layers, used on the Florida dashboard.",
        "Geospatial cloud computing [Esri ArcGIS online v.1.1]: Geospatial cloud system used to efficiently organize and access HRL data through the Esri dashboard."
      ],
      image: serverFlow,
    },
    toa: {
      title: "TOA",
      paragraphs: [
        "The Detection and Location (ToA) Team develops and maintains the core algorithms that form the foundation of the FNN lightning detection pipeline. Operating at the interface between the detectors and the Server Team’s real-time system, we ensure accurate event detection and precise geolocation of lightning discharges.",
        "At the real-time level, the team’s algorithms are delivered to Server to handle the full processing chain: Peak Detection (for high-frequency electric field signals), LCC Detection (for low-frequency electric field analysis), Cluster (time-match peaks across multiple stations), Solver (responsible for calculating lightning locations using Time of Arrival data), and LS Match (co-developed with the Server Team to integrate all results submitted to the server pipeline).",
        "For offline analysis, the team develops and operates advanced tools to evaluate and refine detection performance. These include: FNN Plot-Bin (for waveform visualization and preprocessing), the ToA Dashboard (for assessing station detection metrics and algorithm performance in location efficiency and accuracy), and FNN Monte Carlo (for simulating network behavior using real station capabilities).",
        "For the FFS pipeline the implementation of the new IC/CG and LCC algorithms will guarantee an even better performance."
      ],
      image: toaFlow,
    },
    ai: {
      title: "AI",
      paragraphs: [
        "The AI Team is responsible for maintaining the HRL database, data pipelines, and AI models. Their goal is to process and deliver lightning data received from the Server Team to both the Customers and the GIS Team through API endpoints. In addition to managing data flow, the team analyses the historical datasets, identifies and integrates new data sources to improve AI model performance.",
        "In addition to managing data flow, the team analyses the historical datasets, identifies and integrates new data sources, and improves model performance. The model is trained on environmental and strike-related data to estimate the severity of lightning strikes. This output is known as High-Risk Lightning (HRL) index. It shows the relative likelihood that a lightning strike will ignite a fire.",
      ],
      bullets: [
        "Intracloud/Cloud-to-Ground model helps identify the type of lightning strike.",
        "FFS HRL model is trained on environmental and strike-related data to estimate the severity of lightning strikes. This output is known as High-Risk Lightning (HRL) index. It shows the relative likelihood that a lightning strike will ignite a fire."
      ],
      image: aiFlow,
    },
    gis: {
      title: "GIS",
      paragraphs: [
        "The GIS (Geographic Information Systems) Team specializes in spatial data analysis and visualization to support decision-making and operational awareness. Our core responsibility is the development and maintenance of interactive dashboards within the ESRI environment, focusing on real-time and historical lightning and fire incident tracking.",
        "For the Florida Forest Service pipeline:",
      ],
      bullets: [
        "The HRL data received from the AI team is prepared and displayed on the Esri dashboard. This is the lightning data output seen by the customer.",
        "The fire point data from Mr. Kern’s spreadsheet is processed and the corresponding fire perimeters are drawn using Sentinel images. This is the fire data output seen by the customer."
      ],
      image: gisFlow,
    },
    marketing: {
      title: "Marketing",
      paragraphs: [
        "The Marketing Team drives FNN’s products, storytelling, outreach, and brand visibility across utilities, government agencies, and climate resilience sectors. Our core responsibility is translating complex technical capabilities—such as High-Risk-Lightning (HRL:tm:) detection, edge-AI analysis, and Wildfire Hub intelligence—into clear, impactful narratives that resonate with diverse stakeholders.",
        "The team also manages all product training and pricing communications, including the Starter, Pro, and Enterprise plan details that define FNN’s service tiers and customer engagement models. For the Florida Forest Service:"
      ],
      bullets: [
        "Enterprise Plan has been deployed.",
        "Esri dashboard, fiResponse, and ForeFlight access is provided along with associated training.",
        "Biweekly meetings are held with the client POCs (Monika and Allan).",
        "Client success is summarized through case studies and white papers."
      ],
      image: marketingFlow,
    },
    manufacturing: {
      title: "Manufacturing",
      paragraphs: [
        "The Manufacturing Team for the building block of FNN. This is where the designs by the hardware team get built both as samples and also in larger batch manufacturing. Once the Hardware Team approves the samples, then the Manufacturing Team assures that everything can be scaled keeping at the forefront: flexibility, cost, rate, and quality. The Manufacturing Team is furthermore also directly responsible for proper inventory management and collaborates with the Business Development Team on assuring that there are always enough detectors to satisfy customer demand.",
        "For the Florida Forest Service: ",
      ],
      bullets: [
        "Expansion and detector placement maps are developed to ensure ideal placement.",
        "There are 55 detectors installed. They are maintained regularly and provided with upgrade packages.",
        "For the 20 new v6.1 detectors all of the components are being ordered."
      ],
      image: manufactuingFlow,
    },
  };

  const formatRegionName = (name: string) =>
    name
      .replace(/^detectors_/, "")
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  return (
    <div className="main-hub-wrapper">
      <TeamHeader />
      <MainHub onRegionChange={setSelectedRegions} />

      {/* <div className="rep-wrapper">
        <div className="main-hub-report">
          <h1>Strikes</h1>
          {selectedRegions.map((region) => (
            <div className="main-hub-lf-card" key={region}>
              <h1>{formatRegionName(region)}</h1>
              <div className="main-hub-lf-stats">
                <div className="main-hub-lf-stat">
                  <p>Cloud-to-Ground</p>
                  <h2>{placeholderStats.strikes.cloudToGround}</h2>
                </div>
                <div className="main-hub-lf-stat">
                  <p>Detection Efficiency</p>
                  <h2>{placeholderStats.strikes.detectionEfficiency}%</h2>
                </div>
                <div className="main-hub-lf-stat">
                  <p>Location Accuracy</p>
                  <h2>{placeholderStats.strikes.locationAccuracy}%</h2>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="main-hub-report">
          <h1>Fires</h1>
          {selectedRegions.map((region) => (
            <div className="main-hub-lf-card" key={region}>
              <h1>{formatRegionName(region)}</h1>
              <div className="main-hub-lf-stats">
                <div className="main-hub-lf-stat">
                  <p>Lightning Ignited</p>
                  <h2>{placeholderStats.fires.lightningIgnited}</h2>
                </div>
                <div className="main-hub-lf-stat">
                  <p>LCC Percentage</p>
                  <h2>{placeholderStats.fires.lccPercentage}%</h2>
                </div>
                <div className="main-hub-lf-stat">
                  <p>LCC Ratio</p>
                  <h2>{placeholderStats.fires.lccRatio}</h2>
                </div>
                <div className="main-hub-lf-stat">
                  <p>HRL Rating Median</p>
                  <h2>{placeholderStats.fires.hrlRatingMedian}%</h2>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div> */}

      {/* FLOW SECTION */}
      <div className="main-flow-container">

        <h1 className="cutomer-title" style={{ marginBottom: '0.5rem', marginTop: '1.5rem' }}>Pipelines</h1>

        {Object.entries(flowSections).map(([key, section]) => {
          const isOpen = openSlips.includes(key);
          return (
            <div key={key} style={{ width: "100%" }}>
              <div
                className={`main-flow-slip ${isOpen ? "active" : ""}`}
                onClick={() => toggleSlip(key)}
              >
                <div className="main-flow-slip-header">
                  <span>{section.title}</span>
                  {isOpen ? (
                    <RiArrowDownSFill size={20} className="flow-arrow-icon" />
                  ) : (
                    <RiArrowDownSFill size={20} className="flow-arrow-icon" />
                  )}
                </div>
              </div>

              {isOpen && (
                <div className="main-flow-slip-opened-content">
                  <div className="main-flow-slip-opened-content-left">
                    {section.paragraphs.map((text, idx) => (
                      <p key={`p-${idx}`}>{text}</p>
                    ))}

                    {section.bullets && (
                      <ul className="tight-bullet-list">
                        {section.bullets.map((item, idx) => (
                          <li key={`b-${idx}`}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="main-flow-slip-opened-content-right">
                    <img src={section.image} alt={`${section.title} flow`} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MainHubPage;
